import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Feather, ArrowRight, ArrowLeft, X, Check, Sparkles } from "lucide-react";
import api from "@/lib/api";

// Ordered spotlight steps. `sel` is a list of testid fallbacks (desktop → mobile).
const TOUR_STEPS = [
  {
    page: "dashboard",
    sel: ['[data-tour="dashboard-desk"]'],
    title: "Your writing desk",
    body: "All your books live here. Each one opens into its own writing studio.",
  },
  {
    page: "dashboard",
    sel: ['[data-testid="new-project-button"]', '[data-testid="empty-create-button"]'],
    title: "Start a book",
    body: "Create a new book here — give it a title and you're writing.",
  },
  {
    page: "studio",
    sel: ['[data-testid="view-blueprint"]', '[data-testid="mobile-view-blueprint"]'],
    title: "Blueprint",
    body: "Plan your story — acts, chapters and beats — before or while you write.",
  },
  {
    page: "studio",
    sel: ['[data-testid="view-write"]', '[data-testid="mobile-view-write"]'],
    title: "Write",
    body: "Your manuscript editor. It auto-saves as you type and can read your work aloud.",
  },
  {
    page: "studio",
    sel: ['[data-testid="view-codex"]', '[data-testid="mobile-view-codex"]'],
    title: "Codex",
    body: "Your story's memory — characters, places and things, remembered as you write.",
  },
  {
    page: "studio",
    sel: ['[data-testid="view-inventory"]', '[data-testid="mobile-view-inventory"]'],
    title: "Loadouts",
    body: "Track who's carrying what — Gear Sync keeps weapons, kit and ammo consistent.",
  },
];

const TourContext = createContext(null);
export const useTour = () => useContext(TourContext);

const findEl = (sels) => {
  for (const s of sels) {
    const el = document.querySelector(s);
    if (el && (el.offsetParent !== null || el.getClientRects().length)) return el;
  }
  return null;
};

export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [needProject, setNeedProject] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navigatingRef = useRef(false);

  const total = TOUR_STEPS.length;
  const onStudio = /^\/project\//.test(location.pathname);
  const onDash = location.pathname === "/app";

  const startTour = useCallback(() => {
    setNeedProject(false);
    setIndex(0);
    setActive(true);
  }, []);

  const endTour = useCallback(() => {
    setActive(false);
    setNeedProject(false);
  }, []);

  const next = useCallback(() => setIndex((i) => i + 1), []);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Cross-page navigation to keep the current step's page in view.
  useEffect(() => {
    if (!active || index >= total) return;
    const step = TOUR_STEPS[index];
    if (step.page === "studio" && !onStudio) {
      if (navigatingRef.current) return;
      navigatingRef.current = true;
      (async () => {
        try {
          const { data } = await api.get("/projects");
          if (data && data.length) {
            setNeedProject(false);
            navigate(`/project/${data[0].id}`);
          } else {
            setNeedProject(true);
          }
        } catch {
          setNeedProject(true);
        } finally {
          navigatingRef.current = false;
        }
      })();
    } else if (step.page === "dashboard" && !onDash) {
      navigate("/app");
    } else {
      setNeedProject(false);
    }
  }, [active, index, onStudio, onDash, total, navigate]);

  // Keep the spotlight rectangle glued to the target element.
  useEffect(() => {
    if (!active || index >= total) { setRect(null); return; }
    let raf;
    const measure = () => {
      const step = TOUR_STEPS[index];
      const el = findEl(step.sel);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (needProject && step.page === "studio" && onDash) {
        const np = findEl(['[data-testid="new-project-button"]', '[data-testid="empty-create-button"]']);
        if (np) {
          const r = np.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        } else setRect(null);
      } else {
        setRect(null);
      }
    };
    measure();
    const el = findEl(TOUR_STEPS[index].sel);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const iv = setInterval(measure, 250);
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      clearInterval(iv);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [active, index, total, needProject, onDash]);

  const done = active && index >= total;
  const waitingForProject = active && !done && needProject && TOUR_STEPS[index]?.page === "studio" && onDash;

  return (
    <TourContext.Provider value={{ startTour, active }}>
      {children}
      {active && (
        <TourOverlay
          done={done}
          rect={rect}
          index={index}
          total={total}
          step={TOUR_STEPS[index]}
          waitingForProject={waitingForProject}
          onNext={next}
          onBack={back}
          onEnd={endTour}
        />
      )}
    </TourContext.Provider>
  );
}

function TourOverlay({ done, rect, index, total, step, waitingForProject, onNext, onBack, onEnd }) {
  // Completion card
  if (done) {
    return (
      <div className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4" data-testid="tour-done">
        <div className="bg-popover border border-border rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center fade-up">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-primary-foreground" />
          </div>
          <h3 className="font-display text-2xl font-bold mb-1.5">You're all set!</h3>
          <p className="text-sm text-muted-foreground mb-5">
            That's the grand tour. You can replay it anytime from the menu under your avatar, and
            reach us via <span className="font-semibold text-foreground">"Get help"</span> whenever you need a hand.
          </p>
          <Button data-testid="tour-finish-button" className="w-full h-11 font-semibold" onClick={onEnd}>
            <Sparkles className="w-4 h-4 mr-2" /> Start writing
          </Button>
        </div>
      </div>
    );
  }

  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const cardW = Math.min(320, vw - 24);

  // Decide tooltip placement relative to the highlighted rect.
  let cardStyle;
  if (rect) {
    const below = rect.top + rect.height + 200 < vh || rect.top < vh * 0.4;
    const top = below ? rect.top + rect.height + 12 : Math.max(12, rect.top - 12 - 190);
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(12, Math.min(left, vw - cardW - 12));
    cardStyle = { top, left, width: cardW };
  } else {
    cardStyle = { top: vh / 2 - 90, left: vw / 2 - cardW / 2, width: cardW };
  }

  const isLast = index === total - 1;

  return (
    <>
      {/* click blocker */}
      <div className="fixed inset-0 z-[9990]" style={{ pointerEvents: "auto" }} />
      {/* spotlight cutout via big box-shadow */}
      {rect && (
        <div
          className="fixed z-[9991] rounded-lg pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.68)",
            border: "2px solid hsl(var(--primary))",
          }}
        />
      )}
      {!rect && <div className="fixed inset-0 z-[9991] bg-black/68 pointer-events-none" />}

      {/* tooltip */}
      <div
        data-testid="tour-tooltip"
        className="fixed z-[9995] bg-popover border border-border rounded-xl shadow-2xl p-4 fade-up"
        style={cardStyle}
      >
        <div className="flex items-start gap-2.5 mb-2">
          <div className="w-8 h-8 shrink-0 rounded-lg bg-primary flex items-center justify-center">
            <Feather className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-primary">
              Step {index + 1} of {total}
            </p>
            <h4 className="font-display text-lg font-bold leading-tight">{step.title}</h4>
          </div>
          <button
            data-testid="tour-skip-button"
            onClick={onEnd}
            className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
            title="Skip the tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-3">
          {waitingForProject
            ? "Create your first book (click the highlighted button) and the tour will carry on inside the studio."
            : step.body}
        </p>

        <div className="flex items-center gap-2">
          {index > 0 && (
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="tour-back-button" className="gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Button>
          )}
          <button
            onClick={onEnd}
            data-testid="tour-skip-link"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto mr-1"
          >
            Skip
          </button>
          {!waitingForProject && (
            <Button size="sm" onClick={onNext} data-testid="tour-next-button" className="gap-1 font-semibold">
              {isLast ? "Finish" : "Next"} <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
